import { useEffect, useState } from 'preact/hooks';
import { PlaceholderScreen } from './PlaceholderScreen';
import type { ProjectPhase } from '../routes/route';
import { listRecordsByType, listMedia, ApiError, type RecordRow } from '../lib/api';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { PHASE_PILL } from './projectPresentation';
import { PosterArt } from '../components/PosterImage';
import './Projects.css';

/** Opens the generic create form for a new project (RecordForm engine). */
function newProject() {
  openRecordEditor({ mode: 'create', typeKey: 'actor_project' });
}

export interface ProjectsProps {
  tenantId: string;
  phase: ProjectPhase;
  onOpen: (id: string) => void;
  // R1b — the id of the poster shown in the detail pane (two-pane wide mode),
  // so this grid can mark it selected. Undefined on phone / bare list.
  selectedId?: string;
}

// B3: the phase Strip's ids -> actor_project's own lifecycle_state values
// (packages/verticals/actor/src/index.ts: auditioned/booked/released). 'all'
// deliberately has no entry — GET /records omits the lifecycle_state filter
// entirely for it, rather than inventing a value that isn't a real state.
const PHASE_TO_LIFECYCLE: Partial<Record<ProjectPhase, string>> = {
  auditioned: 'auditioned',
  booked: 'booked',
  released: 'released',
};

const PHASE_HEADER: Record<ProjectPhase, string> = {
  all: 'All',
  auditioned: 'Auditioned',
  booked: 'Booked',
  released: 'Released',
};

const COPY: Record<ProjectPhase, { line: string; soft?: string }> = {
  all: { line: 'No projects yet.', soft: 'Bookings and credits will build this list.' },
  auditioned: { line: 'Nothing auditioned yet.' },
  booked: { line: 'Nothing booked yet.' },
  released: { line: 'Nothing released yet.' },
};

type LoadState = 'loading' | 'ready' | 'error';

function fieldText(record: RecordRow, name: string): string | null {
  const value = record.fields[name];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

// The poster grid, filtered by the phase Strip (B1). All is the default (no
// lifecycle_state filter). Reverse-chron ("newest first", per the demo) —
// the generic GET /records is oldest-first, reversed here for this surface.
export function Projects({ tenantId, phase, onOpen, selectedId }: ProjectsProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [records, setRecords] = useState<RecordRow[]>([]);
  // WS5 — the current poster media id per project (newest kind='poster' wins),
  // fetched once for the whole grid so each card renders its uploaded art.
  const [posterByProject, setPosterByProject] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // R3 — a create/edit/act anywhere bumps this; re-run the list read so a new
  // or edited project appears without a manual refresh.
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState('loading');
      try {
        const rows = await listRecordsByType(tenantId, 'actor_project', PHASE_TO_LIFECYCLE[phase]);
        if (cancelled) return;
        setRecords([...rows].reverse());
        setState('ready');
        // Posters are additive: a failure here never blocks the grid — cards
        // just fall back to the gradient. listMedia is newest-first, so the
        // FIRST poster seen per project is that project's current one.
        try {
          const posters = await listMedia(tenantId, 'poster');
          if (cancelled) return;
          const map: Record<string, string> = {};
          for (const m of posters) {
            if (m.projectId && !(m.projectId in map)) map[m.projectId] = m.id;
          }
          setPosterByProject(map);
        } catch {
          /* gradient fallback */
        }
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setState('error');
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, phase, recordsVersion]);

  const copy = COPY[phase];

  if (state === 'loading') {
    return <PlaceholderScreen eyebrow="Projects" line="One moment…" />;
  }

  if (state === 'error') {
    return <PlaceholderScreen eyebrow="Projects" line="Couldn't load projects." soft={errorMessage ?? undefined} />;
  }

  if (records.length === 0) {
    return (
      <div class="projects">
        <PlaceholderScreen eyebrow="Projects" line={copy.line} soft={copy.soft} />
        <div class="projects__empty-cta">
          <button type="button" class="list-new-btn" onClick={newProject} data-testid="new-project">
            + New project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div class="projects">
      <div class="projects__eye">
        <span>
          Projects · {PHASE_HEADER[phase]} · {records.length}
        </span>
        <button type="button" class="list-new-btn" onClick={newProject} data-testid="new-project">
          + New
        </button>
      </div>
      <div class="projects__grid">
        {records.map((record) => {
          const title = fieldText(record, 'title') ?? record.display_name;
          const role = fieldText(record, 'role');
          const year = fieldText(record, 'air_date') ?? fieldText(record, 'season');
          const medium = fieldText(record, 'medium');
          const lifecycle = record.lifecycle_state ?? 'auditioned';
          const pill = PHASE_PILL[lifecycle] ?? { label: lifecycle, cls: 'aud' };
          const active = record.id === selectedId;
          return (
            <button
              type="button"
              key={record.id}
              class={active ? 'poster poster--active' : 'poster'}
              aria-current={active ? 'true' : undefined}
              onClick={() => onOpen(record.id)}
            >
              <PosterArt
                tenantId={tenantId}
                mediaId={posterByProject[record.id]}
                medium={medium}
                year={year}
                alt={`${title} poster`}
              />
              <div class="poster__meta">
                <div class="poster__t">{title}</div>
                {role && <div class="poster__r">{role}</div>}
                <div class="poster__pill-row">
                  <span class={`ph ph--${pill.cls}`}>{pill.label}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
